// Considered: bare "check-in" rows (the pre-spec state — see Problem Statement) — rejected.
// Every row names its aspect and owner; a bare "check-in" with no subject is exactly what this
// replaces. See spec Reuse Audit (specs/stream-trajectory-tools.md § New components — justified).
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:238-263. Lead row (first, most
// urgent) is visually promoted; manual_checkin demands owned by the viewer carry an inline
// check-in control so the demand can be discharged without leaving the stack.

import React from 'react';
import { useTranslation } from 'localization';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import CheckInInlineMolecule from '../CheckInInline/CheckInInlineMolecule';
import type { InspectorPayload } from 'types/inspector';
import type { TrajectoryDemand } from 'types/trajectory';
import './DemandStackMolecule.scss';

export interface DemandStackMoleculeProps {
  sphereId: string;
  demands: TrajectoryDemand[];
  buildPayload: (demand: TrajectoryDemand) => InspectorPayload;
  onInspect?: (payload: InspectorPayload) => void;
  onCheckInSubmitted?: (demand: TrajectoryDemand) => void;
  className?: string;
}

function ownerLabel(demand: TrajectoryDemand, you: string, automatic: string): string {
  if (demand.owner.automatic) return automatic;
  if (demand.owner.isViewer) return you;
  return demand.owner.name ?? '';
}

const DemandStackMolecule: React.FC<DemandStackMoleculeProps> = ({
  sphereId,
  demands,
  buildPayload,
  onInspect,
  onCheckInSubmitted,
  className,
}) => {
  const { t } = useTranslation();
  const you = t('trajectory.demand.you', { defaultValue: 'you' });
  const automatic = t('trajectory.demand.automatic', { defaultValue: 'automatic' });

  return (
    <div className={['demand-stack-molecule', className].filter(Boolean).join(' ')}>
      {demands.map((demand, index) => {
        const isLead = index === 0;
        const owner = ownerLabel(demand, you, automatic);
        const showsInlineCheckIn = demand.kind === 'manual_checkin' && demand.owner.isViewer && demand.factorId;

        return (
          <div
            key={`${demand.aspectSystemName}-${demand.periodId ?? demand.dueAt}`}
            className={['demand-stack-molecule__row', isLead && 'demand-stack-molecule__row--lead'].filter(Boolean).join(' ')}
          >
            <InspectableMolecule
              payload={buildPayload(demand)}
              onInspect={onInspect}
              peek={t('trajectory.demand.peek', { aspect: demand.label, owner, defaultValue: `${demand.label} owed by ${owner}` })}
              block
              aria-label={`${demand.label} — ${owner}`}
              className="demand-stack-molecule__link"
            >
              <span className="demand-stack-molecule__content">
                <GlyphAtom glyph="lucide:clock" />
                <span className="demand-stack-molecule__text">
                  <strong>{demand.label}</strong> <span className="demand-stack-molecule__owner">— {owner}</span>
                </span>
                <span className="demand-stack-molecule__due">
                  {new Date(demand.dueAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </span>
            </InspectableMolecule>

            {showsInlineCheckIn && (
              <div className="demand-stack-molecule__inline-checkin">
                <span className="demand-stack-molecule__manual-label">
                  {t('trajectory.demand.manualCheckIn', { defaultValue: 'MANUAL CHECK-IN' })}
                </span>
                <span className="demand-stack-molecule__inline-checkin-control">
                  <CheckInInlineMolecule
                    sphereId={sphereId}
                    factorId={demand.factorId as string}
                    onSubmitted={() => onCheckInSubmitted?.(demand)}
                  />
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

DemandStackMolecule.displayName = 'DemandStackMolecule';

export default DemandStackMolecule;
